import { Dayjs } from "dayjs";

export interface PermissionsInterface {
  ROLE_CAN_SEE_FINTECH_LINKS?: boolean;
  ROLE_CAN_SEE_ISW_LINKS?: boolean;
  INVALID_ROLE?: boolean;
}

export interface User {
  firstname: string;
  username: string;
  lastname: string;
  email: string;
  userType: UserType;
  domainCode: string;
}

export type UserType = "ISW_ADMIN";

export type ErrorResponse = {
  error?: boolean;
  message?: string;
};

export interface RequestResponse<T = any> extends ErrorResponse {
  data: T | null;
}

export interface ResourceFetchParams {
  pageNumber: number;
  pageSize: number;
  searchStr?: string;
  code?: string;
}

export interface Issuer {
  id: number;
  name: string;
  issuerCode: string;
  description: string;
  keys?: AppKey[];
}

export const allKeyCategories = ["ZMK", "IMK", "KEK", "ICCKEK", "RSA"] as const;

export type AppKeyCategory = (typeof allKeyCategories)[number];

export const allKeyTypes = [
  "ZMKI",
  "KEK",
  "IMKAC",
  "IMKI",
  "IMKC",
  "IMKDN",
] as const;

export type AppKeyType = (typeof allKeyTypes)[number];

export const allSchemes = [
  "VISA",
  "MASTERCARD",
  "VERVE",
  "AMEX",
  "JCB",
  "UNION_PAY",
  "DISCOVER",
] as const;

export type Scheme = (typeof allSchemes)[number];

export interface AppKey {
  id: number;
  issuerId: number;
  keyLabel: string;
  keyType: AppKeyType | null;
  keyValue: string | null;
  keyVersion: string;
  keyIndex: string;
  keyLength: number;
  keyDescription: string;
  expiryDate: string;
  keyExponentValue?: number;
  issuerBin?: string;
  envFlag: "TEST" | "LIVE";
  kcv?: string;
  keyValueUnderLmk?: string;
  keyCategory: AppKeyCategory | null;
  hasKey: boolean;
  activated: boolean;
  desKeyInfoId?: number;
  keyTypeCode?: string;
  lastUpdatedOn?: string;
}

export interface AppKeyImport {
  keyLabel: string;
  keyValue: string;
  keyValueConfirm?: string;
  kcv: string;
  parentLabel?: string;
  encryptionMode?: string;
  keyScheme?: string;
}

export interface AppKeyCreate
  extends Omit<
    AppKey,
    | "id"
    | "issuerId"
    | "hasKey"
    | "activated"
    | "keyType"
    | "keyDescription"
    | "keyCategory"
  > {
  desKeyInfoId: number;
  keyDescription?: string;
}

export type IssuerCreate = Omit<Issuer, "id">;

export interface CAKey {
  id: number;
  keyLabel: string;
  rid: string;
  keyIndex: string;
  hashAlgIndicator: string;
  keyAlgIndicator: string;
  keyModulus: string;
  keyExponent: string;
  keyCheckSum: string;
  expiryDate: string | null;
  activated: boolean;
  cardScheme: Scheme;
  description: string | null;
}

export interface CertifcateInfo {
  createdOn: Dayjs;
  lastUpdatedOn: Dayjs;
  caPublicKeyId: number;
  expiryDate: string;
  issuerKeyLabel: string;
}

export interface KeyCategory {
  id: number;
  createdOn: string | null;
  lastUpdatedOn: string | null;
  keyCategory: string;
  keyType: string;
  keyTypeCode: string;
  description: string;
}

export type KeyCategoryCreate = Omit<
  KeyCategory,
  "id" | "createdOn" | "lastUpdatedOn"
>;

export interface MigrateFile {
  id: number;
  createdOn: string;
  lastUpdatedOn: string;
  actor: string;
  fileName: string;
  summary: string;
  status: string;
  startTime: string;
  endTime: string;
}

export interface AllDesKeys {
  id: number;
  createdOn: string;
  lastUpdatedOn: string;
  issuerCode: string;
  expiryDate: string;
  activated: boolean;
  keyLabel: string;
  envFlag: string;
  activationDate: string;
  description: string;
  keyIndex: string;
  keyVersion: string;
  migratedDate: string;
  status: string;
  desKeyInfoId: number;
  keyType: string;
  keyCategory: string;
  keyTypeCode: string;
  parentKey: string;
  keyValueUnderZmk: string;
  keyValueUnderLmk: string;
  keyValueUnderOldLmk: string;
  keyLength: number;
  kcv: string;
  encryptionMode: string;
  auditableName: string;
  auditableId: number;
}

export interface AllRsaKeys {
  id: number;
  createdOn: string;
  lastUpdatedOn: string;
  issuerCode: string;
  expiryDate: string;
  activated: boolean;
  keyLabel: string;
  envFlag: string;
  activationDate: string;
  description: string;
  keyIndex: string;
  keyVersion: string;
  migratedDate: string;
  status: string;
  keyModulusLength: number;
  keyExponentValue: number;
  authenticationData: string;
  hashIdentifier: string;
  signatureIdentifier: string;
  issuerBin: string;
  schemeData: string;
  hasKey: boolean;
  hasOldKey: boolean;
  issuerPublicKey: string;
  issuerPrivateKey: string;
  certificateLength: string;
  selfSignedCertificate: string;
  hash: string;
  mac: string;
  oldPrivateKey: string;
  auditableName: string;
  auditableId: number;
}
